import { useState } from 'react';

import LoaderButton from 'components/Button/LoaderButton';
import { RegisterImplementationProps } from 'constants/types';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { AnalyticsEventName, track } from 'utils/analytics';
import { useOAuthStatePolling } from '../../OAuth/useOAuthStatePolling';
import SignInBase from '../SignInBase';
import { getProviderFormLabel } from '../utils';

// Shape of provider.oauthCredentials in the validate response. HiBob's
// marketplace install URL is its authorize endpoint and is the same for every
// developer; only `app_id` differs, and that comes off the developer's
// credential row. The backend picks the URL per environment, so a provider
// sandbox connection arrives here already carrying `mode=dev`. The OAuth client
// id is only used by the backend on the token exchange. The installed common
// types predate these.
export type BobOAuthCredentials = {
  appId?: string | null;
  authorizeUrl?: string | null;
  clientId?: string | null;
  redirectUri?: string | null;
  scopes?: string[] | null;
};

export const getOAuthUrl = ({
  config,
  sessionKey,
}: {
  config: BobOAuthCredentials | null;
  sessionKey: string;
}) => {
  // `scopes` is required rather than defaulted. The backend computes the set
  // from the session's products, so there is no list Connect could fall back to
  // that would be right for every session: guessing wider than the developer's
  // app registration is what produces the mismatch error, and guessing narrower
  // silently drops data.
  if (
    !config?.authorizeUrl ||
    !config?.appId ||
    !config?.redirectUri ||
    !config?.scopes?.length
  ) {
    throw new Error('Invalid OAuth config');
  }

  let url: URL;
  try {
    url = new URL(config.authorizeUrl);
  } catch {
    throw new Error('Invalid OAuth config');
  }

  url.searchParams.set('app_id', config.appId);
  url.searchParams.set('scope', config.scopes.join(' '));
  // The redirect_uri must match the one the backend sends on the token
  // exchange, and state carries the Connect session. HiBob answers "An
  // unexpected error occurred" to the JSON-shaped state the other sign-ins use,
  // so the bare session key goes on the wire; the callback hook accepts either
  // form.
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('state', sessionKey);

  return url.toString();
};

const RegisterBobOAuth = ({
  provider,
  onSubmit,
  setError,
  sessionKey,
  onMockOAuth,
}: RegisterImplementationProps & AuthorizeContextValue) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const config = provider.oauthCredentials as BobOAuthCredentials | null;

  const onCodeReceived = async (clientCode: string) => {
    await onSubmit({
      clientCode,
      // HiBob matches the redirect_uri on the token exchange against the one
      // sent to the installation URL, so both legs use the registered value.
      providerRedirectUri: config?.redirectUri ?? undefined,
    });
  };

  const { loading, openOAuthWindow } = useOAuthStatePolling({
    sessionKey,
    onCodeReceived,
    setError,
  });

  const onOAuthClick = async () => {
    setButtonLoading(true);
    const didMockOAuth = await onMockOAuth();

    if (didMockOAuth) {
      setButtonLoading(false);
      return;
    }

    const oauthUrl = getOAuthUrl({ config, sessionKey });

    track(AnalyticsEventName.RedirectedToOAuthUrl, {
      oauthUrl,
    });

    openOAuthWindow(oauthUrl);
    setButtonLoading(false);
  };

  const instructions = (
    <p>
      HiBob authentication will continue in a new window. Please make sure to
      allow popups, then install the app and approve access in HiBob. You will
      need to be a HiBob admin to complete this step.
    </p>
  );

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      instructions={instructions}
    >
      <div
        style={{
          marginTop: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoaderButton
          isLoading={loading || buttonLoading}
          disabled={loading || buttonLoading}
          onClick={onOAuthClick}
          text="Connect"
        />
      </div>
    </SignInBase>
  );
};

export default withAuthorizeContext(RegisterBobOAuth);
